import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import type { DigestRow } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

type Props = {
  row: DigestRow | null;
  deliveryTime?: string;
  onCancel: () => void;
};

export const DigestHighlightsDialog: React.FC<Props> = ({ row, deliveryTime, onCancel }) => {
  const title = row ? `${formatNewsDate(row.date, { absoluteOnly: true })} — Daily Digest` : "Daily Digest";
  const subtitle = row && deliveryTime ? `${row.highlights.length} highlights · Delivered at ${deliveryTime}` : null;

  return (
    <Box
      sx={{
        width: "min(680px, 94vw)",
        maxHeight: "min(720px, 92vh)",
        display: "flex",
        flexDirection: "column" as const,
        bgcolor: "#fff",
        borderRadius: "10px",
        boxSizing: "border-box" as const
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        sx={{ px: "28px", pt: "24px", pb: "20px", borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}
      >
        <Stack direction="column" gap="2px" sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: "#071f35"
            }}
          >
            {title}
          </Typography>
          {Boolean(subtitle) && (
            <Typography
              sx={{
                fontSize: 12,
                color: "#797979",
                mt: "2px"
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
            color: "secondary.main",
            "&:hover": { bgcolor: "action.hover" },
            "& .MuiSvgIcon-root": { fontSize: 16 }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        direction="column"
        gap="12px"
        sx={{
          px: "28px",
          py: "20px",
          flex: 1,
          minHeight: 0,
          overflowY: "auto"
        }}
      >
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: "4px" }}>
          {row?.highlights?.map((highlight, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 13,
                color: "#071f35",
                pl: "13px",
                position: "relative",
                "&::before": {
                  // eslint-disable-next-line quotes
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "10px",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  bgcolor: "#c09560"
                }
              }}
            >
              {highlight}
            </Box>
          ))}
        </Box>
        {Boolean(row?.summary) && (
          <Typography
            sx={{
              mt: "14px",
              pt: "12px",
              borderTop: "1px solid",
              borderColor: "divider",
              fontSize: 13,
              color: "#071f35"
            }}
          >
            {row?.summary}
          </Typography>
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="8px"
        sx={{
          px: "28px",
          py: "16px",
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0
        }}
      >
        <Button variant="text" onClick={onCancel} sx={{ color: "secondary.main", textTransform: "none" }}>
          Close
        </Button>
      </Stack>
    </Box>
  );
};
